import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, Mail } from 'lucide-react';
import { DEFAULT_BRANDING } from '@/lib/branding';

export default function AcceptInvite() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userInfo, setUserInfo] = useState<{
    email: string;
    organizationName: string;
    organizationLogoUrl: string;
    role: string;
    firstName: string;
    lastName: string;
  } | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  useEffect(() => {
    const checkInvite = async () => {
      const { data: { user } } = await supabase.auth.getUser();

      if (user?.user_metadata) {
        setUserInfo({
          email: user.email || '',
          organizationName: user.user_metadata.organization_name || '',
          organizationLogoUrl: user.user_metadata.organization_logo_url || '',
          role: user.user_metadata.role || '',
          firstName: user.user_metadata.first_name || '',
          lastName: user.user_metadata.last_name || '',
        });
      }
    };

    checkInvite();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 8) {
      setError(t('passwordTooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToSetPassword'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl text-center">
              {t('welcomeToOrg', { orgName: userInfo?.organizationName || DEFAULT_BRANDING.displayName })}
            </CardTitle>
            <CardDescription className="text-center">
              {t('accountCreatedRedirecting')}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <img
              src={userInfo?.organizationLogoUrl || DEFAULT_BRANDING.loadingLogoUrl}
              alt={userInfo?.organizationName || DEFAULT_BRANDING.displayName}
              className="h-12"
            />
          </div>
          <CardTitle className="text-2xl text-center">{t('acceptInviteHeading')}</CardTitle>
          <CardDescription className="text-center">
            {userInfo ? (
              <Trans
                i18nKey={
                  userInfo.firstName.trim() || userInfo.lastName.trim() ? 'auth:inviteWelcome' : 'auth:inviteWelcomeNoName'
                }
                values={{
                  firstName: userInfo.firstName,
                  lastName: userInfo.lastName,
                  orgName: userInfo.organizationName,
                  role: userInfo.role,
                }}
                components={{ strong: <strong /> }}
              />
            ) : (
              t('setupPassword')
            )}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {userInfo && (
              <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{userInfo.email}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">{t('createPasswordLabel')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t('enterYourPassword')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                minLength={8}
              />
              <p className="text-xs text-muted-foreground">
                {t('passwordMinLength')}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder={t('confirmYourPassword')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                minLength={8}
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('settingUpAccount')}
                </>
              ) : (
                t('completeSetup')
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
